import React from 'react';
import { GOLD, GOLD_HI, pctLabel } from './agendaStore';

const Panel = ({ title, children, testid, className = '' }) => (
  <div className={`rounded-2xl overflow-hidden ${className}`} data-testid={testid}
    style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.22)' }}>
    <div className="px-3 py-1.5 font-['Cinzel'] uppercase text-center tracking-[0.28em]"
      style={{ background: 'linear-gradient(90deg, #1a1408, #2a1f0a, #1a1408)', color: GOLD_HI, fontSize: 10 }}>
      {title}
    </div>
    {children}
  </div>
);

export const StatBoxes = ({ stats }) => (
  <div className="flex flex-col gap-2 w-[108px] flex-shrink-0">
    {[['Goal', stats.totalGoal, 'agenda-stat-goal'], ['Completed', stats.completed, 'agenda-stat-completed'], ['Left', stats.left, 'agenda-stat-left']].map(([label, v, id]) => (
      <div key={label} className="rounded-xl overflow-hidden text-center" data-testid={id}
        style={{ border: '1px solid rgba(212,175,55,0.25)' }}>
        <div className="py-1 font-['Cinzel'] uppercase tracking-[0.2em]"
          style={{ background: '#1a1408', color: GOLD_HI, fontSize: 9 }}>{label}</div>
        <div className="py-1.5 font-['Outfit'] font-semibold tabular-nums" style={{ color: '#fff', fontSize: 17 }}>{v}</div>
      </div>
    ))}
  </div>
);

export const Donut = ({ stats }) => {
  const p = stats.totalGoal ? stats.completed / stats.totalGoal : 0;
  const R = 46; const C = 2 * Math.PI * R;
  return (
    <div className="flex-1 flex flex-col items-center justify-center" data-testid="agenda-donut">
      <svg viewBox="0 0 120 120" width="100%" style={{ maxWidth: 170 }}>
        <defs>
          <linearGradient id="agDonut" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={GOLD_HI}/><stop offset="1" stopColor="#9C7A24"/>
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="18"/>
        <circle cx="60" cy="60" r={R} fill="none" stroke="url(#agDonut)" strokeWidth="18"
          strokeDasharray={`${C * p} ${C}`} transform="rotate(-90 60 60)"
          style={{ transition: 'stroke-dasharray 0.35s ease-out' }}/>
        <text x="60" y="57" textAnchor="middle" fill="#fff" fontSize="17" fontFamily="Outfit" fontWeight="600">{pctLabel(p)}</text>
        <text x="60" y="72" textAnchor="middle" fill={GOLD_HI} fontSize="7.5" fontFamily="Cinzel" letterSpacing="1.5">COMPLETED</text>
      </svg>
      <div className="flex gap-3 mt-1 font-['Outfit']" style={{ fontSize: 10 }}>
        <span style={{ color: GOLD_HI }}>● Done {pctLabel(p)}</span>
        <span style={{ color: 'rgba(255,255,255,0.5)' }}>● Left {pctLabel(1 - p)}</span>
      </div>
    </div>
  );
};

const Axis = () => (
  <div className="flex flex-col justify-between pr-1 font-['Outfit'] text-right" style={{ fontSize: 7, color: 'rgba(255,255,255,0.4)', height: 96 }}>
    {['100%', '75%', '50%', '25%', '0%'].map((l) => <span key={l}>{l}</span>)}
  </div>
);

export const DailyBars = ({ daily, todayKey }) => (
  <Panel title="Daily Progress" testid="agenda-daily-chart">
    <div className="flex px-2 pt-3 pb-2">
      <Axis/>
      <div className="flex-1 min-w-0">
        <div className="flex items-end gap-[2px]" style={{ height: 96, borderLeft: '1px solid rgba(255,255,255,0.12)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          {daily.map((d) => (
            <div key={d.key} className="flex-1 rounded-t-[2px]"
              style={{
                height: `${Math.max(d.pct * 100, d.pct ? 4 : 0)}%`,
                background: d.key === todayKey ? `linear-gradient(180deg, #FFF4C4, ${GOLD})` : `linear-gradient(180deg, ${GOLD_HI}, #8C6A1E)`,
                transition: 'height 0.3s ease-out',
              }}/>
          ))}
        </div>
        <div className="flex gap-[2px] mt-1">
          {daily.map((d) => (
            <span key={d.key} className="flex-1 text-center font-['Outfit']"
              style={{ fontSize: 6.5, color: d.key === todayKey ? GOLD_HI : 'rgba(255,255,255,0.4)' }}>{d.dowLabel[0]}</span>
          ))}
        </div>
      </div>
    </div>
  </Panel>
);

export const WeeklyBars = ({ weekly }) => (
  <Panel title="Weekly Progress" testid="agenda-weekly-chart">
    <div className="flex px-2 pt-3 pb-2">
      <Axis/>
      <div className="flex-1">
        <div className="flex items-end justify-around gap-3 px-2" style={{ height: 96, borderLeft: '1px solid rgba(255,255,255,0.12)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          {weekly.map((w) => (
            <div key={w.week} className="flex-1 max-w-[34px] rounded-t-[3px] relative"
              style={{ height: `${w.pct * 100}%`, background: `linear-gradient(180deg, ${GOLD_HI}, #8C6A1E)`, transition: 'height 0.3s ease-out' }}>
              {w.pct > 0 && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 font-['Outfit']" style={{ fontSize: 8, color: GOLD_HI }}>{pctLabel(w.pct)}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-around gap-3 px-2 mt-1">
          {weekly.map((w) => (
            <span key={w.week} className="flex-1 max-w-[34px] text-center font-['Outfit'] whitespace-nowrap" style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.55)' }}>Wk {w.week + 1}</span>
          ))}
        </div>
      </div>
    </div>
  </Panel>
);

export { Panel };
